import type { OpcoesCotacao } from '../../lib/cotacao';

export interface CamposSlk {
  produto: string;
  locais: Record<string, { qtd: string; cores: string }>;
  fornecemos: boolean;
  camisetas: Record<string, string>;
  valorArte: string;
  clienteDireto: boolean;
}

type Opcoes = NonNullable<OpcoesCotacao['estamparia']>;

const CORES = ['0', '1', '2', '3', '4', '5'];

export function silkVazio(o: Opcoes): CamposSlk {
  return {
    produto: o.produtos[0] ?? '',
    locais: {},
    fornecemos: false,
    camisetas: {},
    valorArte: '',
    clienteDireto: true,
  };
}

export const silkCompleto = (f: CamposSlk) =>
  Object.values(f.locais).some((l) => Number(l.qtd) > 0 && Number(l.cores) > 0);

function locaisDoProduto(o: Opcoes, produto: string) {
  if (produto === 'Camiseta Fechada') return o.locais.filter((l) => !l.perna);
  if (produto === 'Calça fechada') return o.locais.filter((l) => l.perna);
  return o.locais;
}

export function CamposSilk({
  o,
  f,
  mudar,
}: {
  o: Opcoes;
  f: CamposSlk;
  mudar: (p: Partial<CamposSlk>) => void;
}) {
  const locais = locaisDoProduto(o, f.produto);

  const trocarProduto = (produto: string) => {
    const validos = new Set(locaisDoProduto(o, produto).map((l) => l.id));
    mudar({
      produto,
      locais: Object.fromEntries(Object.entries(f.locais).filter(([id]) => validos.has(id))),
    });
  };
  const mudarLocal = (id: string, p: Partial<{ qtd: string; cores: string }>) =>
    mudar({
      locais: { ...f.locais, [id]: { qtd: '', cores: '0', ...f.locais[id], ...p } },
    });

  return (
    <>
      <div className="campo">
        <span>Peça</span>
        <div className="segmentos" role="radiogroup" aria-label="Peça">
          {o.produtos.map((p) => (
            <button
              key={p}
              role="radio"
              aria-checked={f.produto === p}
              className={f.produto === p ? 'ativo' : ''}
              onClick={() => trocarProduto(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="campo">
        <span>Locais da estampa</span>
        <div className="locais-silk">
          <span className="cab">Local</span>
          <span className="cab">Peças</span>
          <span className="cab">Cores</span>
          {locais.map((l) => (
            <div key={l.id} className="linha-silk">
              <span>{l.nome}</span>
              <input
                className="input"
                inputMode="numeric"
                placeholder="0"
                aria-label={`Peças com estampa em ${l.nome}`}
                value={f.locais[l.id]?.qtd ?? ''}
                onChange={(e) => mudarLocal(l.id, { qtd: e.target.value.replace(/\D/g, '') })}
              />
              <select
                className="input"
                aria-label={`Cores em ${l.nome}`}
                value={f.locais[l.id]?.cores ?? '0'}
                onChange={(e) => mudarLocal(l.id, { cores: e.target.value })}
              >
                {CORES.map((c) => (
                  <option key={c} value={c}>
                    {c === '0' ? '—' : `${c} cor${c === '1' ? '' : 'es'}`}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      <div className="campo">
        <span>Camisetas</span>
        <div className="segmentos" role="radiogroup" aria-label="Camisetas">
          <button
            role="radio"
            aria-checked={!f.fornecemos}
            className={!f.fornecemos ? 'ativo' : ''}
            onClick={() => mudar({ fornecemos: false, camisetas: {} })}
          >
            Cliente tem as peças
          </button>
          <button
            role="radio"
            aria-checked={f.fornecemos}
            className={f.fornecemos ? 'ativo' : ''}
            onClick={() => mudar({ fornecemos: true })}
          >
            Nós fornecemos
          </button>
        </div>
        {f.fornecemos && (
          <div className="tamanhos-silk">
            {o.tamanhos.map((t) => (
              <label key={t} className="campo">
                <span>{t}</span>
                <input
                  className="input"
                  inputMode="numeric"
                  placeholder="0"
                  value={f.camisetas[t] ?? ''}
                  onChange={(e) =>
                    mudar({ camisetas: { ...f.camisetas, [t]: e.target.value.replace(/\D/g, '') } })
                  }
                />
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="cotacao-linha">
        <label className="campo">
          <span>Arte (R$)</span>
          <input
            className="input"
            inputMode="decimal"
            placeholder="0,00"
            value={f.valorArte}
            onChange={(e) => mudar({ valorArte: e.target.value })}
          />
        </label>
        <div className="campo">
          <span>Preço</span>
          <div className="segmentos" role="radiogroup" aria-label="Preço">
            <button
              role="radio"
              aria-checked={f.clienteDireto}
              className={f.clienteDireto ? 'ativo' : ''}
              onClick={() => mudar({ clienteDireto: true })}
            >
              Cliente direto
            </button>
            <button
              role="radio"
              aria-checked={!f.clienteDireto}
              className={!f.clienteDireto ? 'ativo' : ''}
              onClick={() => mudar({ clienteDireto: false })}
            >
              Revenda
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
